import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import Navbar from "./OfficerNavbar";
import "./DataEditing.css";
import Modal from "react-modal";
import { baseurl } from "../../url";
import { useLocation } from "react-router-dom";
import { getFeeCategoryDisplayName } from '../../utils';

Modal.setAppElement("#root");

const DataEntryForm = ({ fetchStudents, onDataEntered }) => {
  const location = useLocation();
  const isAdmissionRoute = location.pathname === "/admission";

  const initialFormData = {
    admissionType: "",
    admissionId: "",
    allotmentCategory: "",
    feeCategory: "",
    name: "",
    photo: null,
    photoPreview: null,
    address: "",
    permanentAddress: "",
    pincode: "",
    religion: "",
    community: "",
    category: "",
    gender: "",
    dateOfBirth: "",
    bloodGroup: "",
    mobileNo: "",
    whatsappNo: "",
    email: "",
    entranceExam: "",
    entranceRollNo: "",
    entranceRank: "",
    aadharNo: "",
    qualify: {
      exam: "",
      board: "",
      regNo: "",
      examMonthYear: "",
      percentage: "",
      cgpa: "",
      institution: "",
    },
    parentDetails: {
      fatherName: "",
      fatherOccupation: "",
      fatherMobileNo: "",
      motherName: "",
      motherOccupation: "",
      motherMobileNo: "",
    },
    annualIncome: "",
    nativity: "",
    bankDetails: {
      bankName: "",
      branch: "",
      accountNo: "",
      ifscCode: "",
    },
    achievements: {
      arts: "",
      sports: "",
      other: "",
    },
    course: "",
    marks: {
      boardType: "",
      physics: "",
      chemistry: "",
      maths: "",
    },
    certificates: {
      tenth: false,
      plusTwo: false,
      tcandconduct: false,
      allotmentmemo: false,
      Datasheet: false,
      physicalfitness: false,
      passportsizephoto: false,
      incomecertificates: false,
      communitycertificate: false,
      castecertificate: false,
      aadhaar: false,
      degreecertificates: false,
      marklist: false,
      other: false,
    },
  };

  const [formData, setFormData] = useState({ ...initialFormData });
  const [currentStep, setCurrentStep] = useState(0);
  const fileInputRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [copyAddressOption, setCopyAddressOption] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  // ── KEY FIX: store the MediaStream in a ref so it's always current ──
  const cameraStreamRef = useRef(null);

  const steps = [
    "Admission & Personal",
    "Contact & Academics",
    "Family & Financial",
    "Documents & Achievements",
    "Preview & Submit",
  ];

  // ── KEY FIX: attach stream to video element whenever showCamera becomes true ──
  useEffect(() => {
    if (showCamera && cameraStreamRef.current && videoRef.current) {
      const video = videoRef.current;
      video.srcObject = cameraStreamRef.current;
      video.onloadedmetadata = () => {
        video.play().catch((err) => console.error("Play error:", err));
      };
    }
  }, [showCamera]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // ── FIXED: stopCamera uses ref, never stale state ──
  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.onloadedmetadata = null;
    }
    setShowCamera(false);
  };

  // ── FIXED: startCamera stores stream in ref immediately, then triggers render ──
  const startCamera = async () => {
    // Stop any existing stream first (ref-based, always current)
    stopCamera();

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });

      // Store in ref BEFORE state update so the useEffect can access it
      cameraStreamRef.current = mediaStream;
      setShowCamera(true); // triggers useEffect which attaches stream to video
    } catch (error) {
      console.error("Error accessing camera:", error);
      setErrorMessage(
        "Unable to access camera. Please check permissions and ensure no other app is using the camera."
      );
      setModalIsOpen(true);
    }
  };

  // ── FIXED: capturePhoto checks readyState before drawing ──
  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) {
      setErrorMessage("Camera error. Please try again.");
      setModalIsOpen(true);
      return;
    }

    // readyState >= 2 means we have at least the current frame
    if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
      setErrorMessage("Camera not ready yet. Please wait a moment and try again.");
      setModalIsOpen(true);
      return;
    }

    try {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext("2d");
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            setErrorMessage("Failed to capture photo. Please try again.");
            setModalIsOpen(true);
            return;
          }

          if (blob.size > 250 * 1024) {
            setErrorMessage(
              "Photo size exceeds 250 KB. Please try again with better lighting or move closer."
            );
            setModalIsOpen(true);
            return;
          }

          // Clean up old preview URL before creating new one
          if (formData.photoPreview) {
            URL.revokeObjectURL(formData.photoPreview);
          }

          const file = new File([blob], "captured-photo.jpg", {
            type: "image/jpeg",
          });
          const previewUrl = URL.createObjectURL(blob);

          setFormData((prev) => ({
            ...prev,
            photo: file,
            photoPreview: previewUrl,
          }));

          setErrorMessage("");
          stopCamera(); // stop stream after successful capture
        },
        "image/jpeg",
        0.85 // slightly lower quality helps stay under 250 KB
      );
    } catch (error) {
      console.error("Capture error:", error);
      setErrorMessage("Error capturing photo. Please try again.");
      setModalIsOpen(true);
    }
  };

  // ── FIXED: retakePhoto doesn't need setTimeout; startCamera is independent ──
  const retakePhoto = () => {
    if (formData.photoPreview) {
      URL.revokeObjectURL(formData.photoPreview);
    }
    setFormData((prev) => ({ ...prev, photo: null, photoPreview: null }));
    startCamera();
  };

  const cancelCamera = () => {
    stopCamera();
  };

  const handleRemovePhoto = () => {
    if (formData.photoPreview) {
      URL.revokeObjectURL(formData.photoPreview);
    }
    setFormData((prev) => ({ ...prev, photo: null, photoPreview: null }));
  };

  const handleFileInputChange = (event) => {
    const { files } = event.target;
    if (files.length > 0) {
      const file = files[0];
      if (file.size > 250 * 1024) {
        setErrorMessage("The file size exceeds 250 KB.");
        setModalIsOpen(true);
      } else {
        if (formData.photoPreview) {
          URL.revokeObjectURL(formData.photoPreview);
        }
        const previewUrl = URL.createObjectURL(file);
        setFormData((prev) => ({
          ...prev,
          photo: file,
          photoPreview: previewUrl,
        }));
        setErrorMessage("");
      }
    }
  };

  const handleCopyAddress = () => {
    if (!copyAddressOption) {
      // When checking - copy address
      setFormData((prev) => ({
        ...prev,
        permanentAddress: prev.address,
      }));
      setCopyAddressOption(true);
    } else {
      // When unchecking - clear the copied address but keep permanent address field
      setFormData((prev) => ({
        ...prev,
        permanentAddress: "",
      }));
      setCopyAddressOption(false);
    }
  };

  const handleChange = (event) => {
    const { name, value, files, checked, type } = event.target;
    if (name === "photo") {
      if (files.length > 0) {
        const file = files[0];
        if (file.size > 250 * 1024) {
          setErrorMessage("The file size exceeds 250 KB.");
          setModalIsOpen(true);
        } else {
          if (formData.photoPreview) URL.revokeObjectURL(formData.photoPreview);
          const previewUrl = URL.createObjectURL(file);
          setFormData((prev) => ({ ...prev, photo: file, photoPreview: previewUrl }));
          setErrorMessage("");
        }
      }
    } else if (name === "address" && copyAddressOption) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else if (name.includes("qualify")) {
      const [, subField] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        qualify: { ...prev.qualify, [subField]: value },
      }));
    } else if (name.includes("parentDetails")) {
      const [, subField] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        parentDetails: { ...prev.parentDetails, [subField]: value },
      }));
    } else if (name.startsWith("bankDetails")) {
      const [, subField] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        bankDetails: { ...prev.bankDetails, [subField]: value },
      }));
    } else if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        certificates: { ...prev.certificates, [name]: checked },
      }));
    } else if (name.includes("marks")) {
      const [, subField] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        marks: { ...prev.marks, [subField]: value },
      }));
    } else if (name.startsWith("achievements")) {
      setFormData((prev) => ({
        ...prev,
        achievements: { ...prev.achievements, [name.split(".")[1]]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    
    console.log("=== Starting form submission ===");
    console.log("Form data before processing:", formData);

    const sendData = new FormData();
    
    // Handle each field properly
    for (const key in formData) {
      if (key === "photoPreview") continue;
      
      if (key === "certificates") {
        // Handle certificates as boolean values
        for (const certKey in formData.certificates) {
          const value = formData.certificates[certKey]; // This is already boolean (true/false)
          sendData.append(`certificates.${certKey}`, value);
          console.log(`Appending: certificates.${certKey} =`, value, typeof value);
        }
      } else if (formData[key] instanceof Object && !(formData[key] instanceof File)) {
        // Handle nested objects (qualify, parentDetails, bankDetails, achievements, marks)
        for (const subKey in formData[key]) {
          const value = formData[key][subKey] || "nil";
          sendData.append(`${key}.${subKey}`, value);
          console.log(`Appending: ${key}.${subKey} =`, value, typeof value);
        }
      } else {
        // Handle primitive values
        const value = formData[key] ? formData[key] : "nil";
        sendData.append(key, value);
        console.log(`Appending: ${key} =`, value, typeof value);
      }
    }

    console.log("FormData entries after building:");
    for (let pair of sendData.entries()) {
      console.log(pair[0], pair[1], typeof pair[1]);
    }

    try {
      console.log("Sending POST request to:", `${baseurl}/api/studentadmission`);
      
      const response = await axios.post(`${baseurl}/api/studentadmission`, sendData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      console.log("Response received:", response);
      console.log("Response status:", response.status);
      console.log("Response data:", response.data);
      
      setFormData({ ...initialFormData });
      setErrorMessage("Data submitted successfully");
      setModalIsOpen(true);
      
      console.log("=== Form submission successful ===");
    } catch (error) {
      console.error("=== Error during form submission ===");
      console.error("Error object:", error);
      
      if (error.response) {
        console.error("Error response status:", error.response.status);
        console.error("Error response data:", error.response.data);
        console.error("Error response headers:", error.response.headers);
        setErrorMessage(error.response.data.message || "Error submitting form");
      } else if (error.request) {
        console.error("No response received:", error.request);
        setErrorMessage("No response from server. Please check your connection.");
      } else {
        console.error("Error setting up request:", error.message);
        setErrorMessage("Error submitting form");
      }
      
      setModalIsOpen(true);
    } finally {
      setLoading(false);
      console.log("=== Form submission completed ===");
      setCurrentStep(0)
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
    if (errorMessage === "Data submitted successfully") {
      window.location.reload();
    }
  };

  const renderPreview = () => (
    <div className="preview-container">
      <div className="preview-section">
        <h4>Student Information </h4>
        <div className="preview-grid">
          <div><strong>Name:</strong> {formData.name || "—"}</div>
          <div><strong>Admission Type:</strong> {formData.admissionType || "—"}</div>
          <div><strong>Allotment Category:</strong> {formData.allotmentCategory || "—"}</div>
          <div><strong>Fee Category:</strong> {getFeeCategoryDisplayName(formData.feeCategory) || "—"}</div>
          <div><strong>Course:</strong> {formData.course || "—"}</div>
          <div><strong>Gender:</strong> {formData.gender || "—"}</div>
          <div><strong>Date of Birth:</strong> {formData.dateOfBirth || "—"}</div>
          <div><strong>Blood Group:</strong> {formData.bloodGroup || "—"}</div>
          <div><strong>Religion:</strong> {formData.religion || "—"}</div>
          <div><strong>Community:</strong> {formData.community || "—"}</div>
          <div><strong>Category:</strong> {formData.category || "—"}</div>
          <div><strong>Aadhar No:</strong> {formData.aadharNo || "—"}</div>
        </div>
      </div>

      <div className="preview-section">
        <h4>Contact Details</h4>
        <div className="preview-grid">
          <div><strong>Mobile:</strong> {formData.mobileNo || "—"}</div>
          <div><strong>WhatsApp:</strong> {formData.whatsappNo || "—"}</div>
          <div><strong>Email:</strong> {formData.email || "—"}</div>
          <div><strong>Address:</strong> {formData.address || "—"}</div>
          <div><strong>Permanent Address:</strong> {formData.permanentAddress || "—"}</div>
          <div><strong>Pincode:</strong> {formData.pincode || "—"}</div>
        </div>
      </div>

      <div className="preview-section">
        <h4>Academic Details</h4>
        <div className="preview-grid">
          <div><strong>Qualifying Exam:</strong> {formData.qualify.exam || "—"}</div>
          <div><strong>Board:</strong> {formData.qualify.board || "—"}</div>
          <div><strong>Institution:</strong> {formData.qualify.institution || "—"}</div>
          <div><strong>Reg No:</strong> {formData.qualify.regNo || "—"}</div>
          <div><strong>Percentage:</strong> {formData.qualify.percentage || "—"}</div>
          <div><strong>CGPA:</strong> {formData.qualify.cgpa || "—"}</div>
          <div><strong>Entrance Exam:</strong> {formData.entranceExam || "—"}</div>
          <div><strong>Entrance Roll No:</strong> {formData.entranceRollNo || "—"}</div>
          <div><strong>Entrance Rank:</strong> {formData.entranceRank || "—"}</div>
        </div>
      </div>

      <div className="preview-section">
        <h4>Family & Financial</h4>
        <div className="preview-grid">
          <div><strong>Father's Name:</strong> {formData.parentDetails.fatherName || "—"}</div>
          <div><strong>Mother's Name:</strong> {formData.parentDetails.motherName || "—"}</div>
          <div><strong>Annual Income:</strong> {formData.annualIncome || "—"}</div>
          <div><strong>Nativity:</strong> {formData.nativity || "—"}</div>
          <div><strong>Bank Name:</strong> {formData.bankDetails.bankName || "—"}</div>
          <div><strong>Account No:</strong> {formData.bankDetails.accountNo || "—"}</div>
          <div><strong>IFSC Code:</strong> {formData.bankDetails.ifscCode || "—"}</div>
        </div>
      </div>

      <div className="preview-section">
        <h4>Certificates Submitted</h4>
        <div className="certificates-preview">
          {Object.entries(formData.certificates)
            .filter(([, value]) => value)
            .map(([key]) => (
              <span key={key} className="cert-badge">
                {key.replace(/([A-Z])/g, " $1").trim()}
              </span>
            ))}
          {!Object.values(formData.certificates).some((v) => v) && (
            <span>— No certificates selected —</span>
          )}
        </div>
      </div>
    </div>
  );

  const renderPhotoSection = () => (
    <div className="form-group">
      <label className="required">Photo:</label>
      <small>(size less than 250 KB)</small>

      {/* Camera overlay */}
      {showCamera && (
        <div className="camera-modal">
          <div className="camera-container">
            <div className="camera-header">
              <h4>Capture Photo</h4>
              <button type="button" className="close-camera" onClick={cancelCamera}>
                ✕
              </button>
            </div>
            <div className="camera-preview">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <canvas ref={canvasRef} style={{ display: "none" }} />
            </div>
            <div className="camera-controls">
              <button type="button" className="capture-btn" onClick={capturePhoto}>
                📸 Capture
              </button>
              <button type="button" className="cancel-btn" onClick={cancelCamera}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo display */}
      <div className="photo-area">
        {formData.photoPreview ? (
          <div className="photo-preview-container">
            <img
              src={formData.photoPreview}
              alt="Student preview"
              className="photo-preview-large"
            />
            <div className="photo-actions">
              <button
                type="button"
                className="photo-action-btn retake"
                onClick={retakePhoto}
              >
                ⟳ Retake
              </button>
              <button
                type="button"
                className="photo-action-btn remove"
                onClick={handleRemovePhoto}
              >
                ✕ Remove
              </button>
            </div>
          </div>
        ) : (
          <div className="photo-upload-area">
            <div className="upload-placeholder">
              <span className="placeholder-icon">📷</span>
              <p>No photo selected</p>
            </div>
            <div className="photo-buttons">
              <button
                type="button"
                className="capture-button"
                onClick={startCamera}
              >
                📸 Take Photo
              </button>
              <input
                ref={fileInputRef}
                type="file"
                name="photo"
                onChange={handleFileInputChange}
                accept="image/jpeg, image/png, image/jpg"
                style={{ display: "none" }}
              />
              <button
                type="button"
                className="upload-button"
                onClick={() => fileInputRef.current?.click()}
              >
                📁 Upload Photo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="step-content">
            <div className="row">
              <div className="form-group">
                <label className="required">Admission Type:</label>
                <select name="admissionType" value={formData.admissionType} onChange={handleChange} required>
                  <option value="">Select Admission Type</option>
                  <option value="KEAM">KEAM</option>
                  <option value="LBS-MCA">LBS-MCA</option>
                  <option value="SPOT">SPOT</option>
                  <option value="LET">LET</option>
                </select>
              </div>
              <div className="form-group">
                <label className="required">Allotment Category:</label>
                <select name="allotmentCategory" value={formData.allotmentCategory} onChange={handleChange} required>
                  <option value="">Select Allotment Category</option>
                  <option value="State Merit">SM</option>
                  <option value="Management">MG</option>
                </select>
              </div>
              <div className="form-group">
                <label className="required">Fee Category:</label>
                <select name="feeCategory" value={formData.feeCategory} onChange={handleChange} required>
                  <option value="">Select Fee Category</option>
                  <option value="Merit Lower Fee">Merit Low Fee</option>
                  <option value="Merit Higher Fee">Merit Full Fee</option>
                  <option value="Fee wavier">FW</option>
                </select>
              </div>
            </div>
            <div className="row">
              <div className="form-group">
                <label className="required">Course:</label>
                <select
                  value={formData.course}
                  onChange={(e) => setFormData((prev) => ({ ...prev, course: e.target.value }))}
                >
                  <option value="">Select Course</option>
                  <option value="B.Tech CSE">B.Tech CSE</option>
                  <option value="B.Tech ECE">B.Tech ECE</option>
                  <option value="BBA">BBA</option>
                  <option value="BCA">BCA</option>
                  <option value="MCA">MCA</option>
                </select>
              </div>
              <div className="form-group">
                <label className="required">Name:</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required />
              </div>
            </div>

            {renderPhotoSection()}

            <div className="row">
              <div className="form-group">
                <label className="required">Date of Birth:</label>
                <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label className="required">Gender:</label>
                <select name="gender" value={formData.gender} onChange={handleChange} required>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Transgender">Transgender</option>
                  <option value="Prefer to not say">Prefer to not say</option>
                </select>
              </div>
              <div className="form-group">
                <label className="required">Blood Group:</label>
                <input type="text" name="bloodGroup" value={formData.bloodGroup} onChange={handleChange} required />
              </div>
            </div>
            <div className="row">
              <div className="form-group">
                <label className="required">Religion:</label>
                <input type="text" name="religion" value={formData.religion} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label className="required">Community:</label>
                <input type="text" name="community" value={formData.community} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label className="required">Category:</label>
                <input type="text" name="category" value={formData.category} onChange={handleChange} required />
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="step-content">
            <div className="form-group">
              <label className="required">Address:</label>
              <textarea name="address" value={formData.address} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={copyAddressOption} 
                  onChange={handleCopyAddress} 
                />
                Copy Address to Permanent Address
              </label>
            </div>
            <div className="form-group">
              <label>Permanent Address:</label>
              <textarea name="permanentAddress" value={formData.permanentAddress} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="required">Pincode:</label>
              <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="required">Mobile No:</label>
              <input type="tel" name="mobileNo" value={formData.mobileNo} onChange={handleChange} required pattern="[0-9]{10}" title="Please enter a valid 10-digit phone number" />
            </div>
            <div className="form-group">
              <label className="required">WhatsApp No:</label>
              <input type="tel" name="whatsappNo" value={formData.whatsappNo} onChange={handleChange} required pattern="[0-9]{10}" title="Please enter a valid 10-digit phone number" />
            </div>
            <div className="form-group">
              <label className="required">Email:</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="required">Aadhar No:</label>
              <input type="text" name="aadharNo" value={formData.aadharNo} onChange={handleChange} pattern="[2-9]{1}[0-9]{3}[0-9]{4}[0-9]{4}" title="Please enter a valid Aadhar number" required />
            </div>
            <div className="box">
              <h4>Qualifying Exam Details</h4>
              <div className="parent-details-row">
                <div className="form-group">
                  <label className="required">Qualifying Exam:</label>
                  <input type="text" name="qualify.exam" value={formData.qualify.exam} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label className="required">Board:</label>
                  <input type="text" name="qualify.board" value={formData.qualify.board} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label className="required">Institution:</label>
                  <input type="text" name="qualify.institution" value={formData.qualify.institution} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label className="required">Reg No:</label>
                  <input type="text" name="qualify.regNo" value={formData.qualify.regNo} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label className="required">Exam Month/Year:</label>
                  <input type="text" name="qualify.examMonthYear" value={formData.qualify.examMonthYear} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label className="required">Percentage:</label>
                  <input type="text" name="qualify.percentage" value={formData.qualify.percentage} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>CGPA:</label>
                  <input type="text" name="qualify.cgpa" value={formData.qualify.cgpa} onChange={handleChange} />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>Entrance Exam Name:</label>
              <input type="text" name="entranceExam" value={formData.entranceExam} onChange={handleChange} />
            </div>
            <div className="parent-details-row">
              <div className="form-group">
                <label>Entrance Roll No:</label>
                <input type="text" name="entranceRollNo" value={formData.entranceRollNo} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Entrance Rank:</label>
                <input type="text" name="entranceRank" value={formData.entranceRank} onChange={handleChange} />
              </div>
            </div>
            {(formData.course === "B.Tech CSE" || formData.course === "B.Tech ECE") && (
              <div className="box">
                <h4>Plus Two Marks</h4>
                <label className="required">(Please Enter Plus Two Mark Only)</label>
                <div className="radio-group-row">
                  {["State", "CBSE", "ICSE", "Others"].map((board) => (
                    <label key={board}>
                      <input type="radio" name="marks.boardType" value={board} checked={formData.marks.boardType === board} onChange={handleChange} />
                      {board}
                    </label>
                  ))}
                </div>
                <div className="row">
                  <div className="form-group">
                    <label>Physics Marks:</label>
                    <input type="number" name="marks.physics" value={formData.marks.physics} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Chemistry Marks:</label>
                    <input type="number" name="marks.chemistry" value={formData.marks.chemistry} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Maths Marks:</label>
                    <input type="number" name="marks.maths" value={formData.marks.maths} onChange={handleChange} />
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 2:
        return (
          <div className="step-content">
            <div className="box">
              <h4>Parents Details</h4>
              <div className="parent-details-row">
                <div className="form-group">
                  <label>Father's Name:</label>
                  <input type="text" name="parentDetails.fatherName" value={formData.parentDetails.fatherName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Mother's Name:</label>
                  <input type="text" name="parentDetails.motherName" value={formData.parentDetails.motherName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Father's Occupation:</label>
                  <input type="text" name="parentDetails.fatherOccupation" value={formData.parentDetails.fatherOccupation} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Mother's Occupation:</label>
                  <input type="text" name="parentDetails.motherOccupation" value={formData.parentDetails.motherOccupation} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Father's Mobile No:</label>
                  <input type="tel" name="parentDetails.fatherMobileNo" value={formData.parentDetails.fatherMobileNo} onChange={handleChange} pattern="[0-9]{10}" title="Please enter a valid 10-digit phone number" />
                </div>
                <div className="form-group">
                  <label>Mother's Mobile No:</label>
                  <input type="tel" name="parentDetails.motherMobileNo" value={formData.parentDetails.motherMobileNo} onChange={handleChange} pattern="[0-9]{10}" title="Please enter a valid 10-digit phone number" />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="form-group">
                <label className="required">Annual Income:</label>
                <input type="text" name="annualIncome" value={formData.annualIncome} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label className="required">Nativity:</label>
                <input type="text" name="nativity" value={formData.nativity} onChange={handleChange} required />
              </div>
            </div>
            <div className="box">
              <h4>Bank Details</h4>
              <div className="parent-details-row">
                <div className="form-group">
                  <label>Bank Name:</label>
                  <input type="text" name="bankDetails.bankName" value={formData.bankDetails.bankName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Branch:</label>
                  <input type="text" name="bankDetails.branch" value={formData.bankDetails.branch} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Account No:</label>
                  <input type="text" name="bankDetails.accountNo" value={formData.bankDetails.accountNo} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>IFSC Code:</label>
                  <input type="text" name="bankDetails.ifscCode" value={formData.bankDetails.ifscCode} onChange={handleChange} />
                </div>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="step-content">
            <div className="box">
              <h4>Achievements</h4>
              <div className="row">
                <div className="form-group">
                  <label>Arts:</label>
                  <input type="text" name="achievements.arts" value={formData.achievements.arts} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Sports:</label>
                  <input type="text" name="achievements.sports" value={formData.achievements.sports} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Other:</label>
                  <input type="text" name="achievements.other" value={formData.achievements.other} onChange={handleChange} />
                </div>
              </div>
            </div>
            <div className="checkbox-container">
              <label className="required">Submitted Certificates</label>
              <h5>(Please Click on the Text to Select)</h5>
              <div className="checkbox-grid">
                {Object.entries(formData.certificates).map(([key, value]) => (
                  <div className="checkbox-custom" key={key}>
                    <input type="checkbox" id={key} name={key} checked={value} onChange={handleChange} />
                    <label htmlFor={key}>
                      {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 4:
        return renderPreview();
      default:
        return null;
    }
  };

  return (
    <>
      {!isAdmissionRoute && <Navbar />}
      <div className="data-entry-container" style={isAdmissionRoute ? { marginTop: '40px' } : {}}>
        <div className="page-title">Admission Form Page</div>
        <hr className="divider" />

        <div className="progress-steps">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`step ${index === currentStep ? "active" : ""} ${index < currentStep ? "completed" : ""}`}
              onClick={() => setCurrentStep(index)}
            >
              <div className="step-number">
                <span>{index + 1}</span>
              </div>
              <div className="step-label">{step}</div>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {renderStep()}
          <div className="form-navigation">
            {currentStep > 0 && (
              <button type="button" className="nav-button prev" onClick={handlePrev}>
                ← Previous
              </button>
            )}
            {currentStep < steps.length - 1 && (
              <button type="button" className="nav-button next" onClick={handleNext}>
                Next →
              </button>
            )}
            {currentStep === steps.length - 1 && (
              <button type="submit" className="nav-button submit">
                ✓ Submit Application
              </button>
            )}
          </div>
        </form>

        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Saving...</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Message Modal"
        className="modal"
        overlayClassName="modal-overlay"
      >
        <h3>{errorMessage === "Data submitted successfully" ? "Success" : "Error"}</h3>
        <p>{errorMessage}</p>
        <button onClick={closeModal}>Close</button>
      </Modal>
    </>
  );
};

export default DataEntryForm;